import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Badge } from 'antd'
import FeatureThemeProvider from '@/components/FeatureThemeProvider'
import { Preppy } from './Preppy'
import { useAssistant } from './AssistantProvider'

/**
 * Preppy's resting place: just the face, on the right edge, no chrome.
 *
 * You can drag it up and down that edge so it never covers what you are working
 * on — horizontally it stays pinned, because a free-floating assistant ends up
 * over the content it is supposed to be helping with. A press that barely moves
 * counts as a click and toggles the panel.
 *
 * Hidden while the panel is open and while a walkthrough runs, since Preppy is
 * visibly somewhere else in both cases.
 */

export const DOCK_SIZE = 54
const EDGE_TOP = 64
const EDGE_BOTTOM = 68
/** Movement under this many px still counts as a click, not a drag. */
const DRAG_THRESHOLD = 3
/** How far the arrow keys nudge it, for people not using a pointer. */
const KEY_STEP = 32

const clampTop = (value: number) =>
  Math.max(EDGE_TOP, Math.min(window.innerHeight - EDGE_BOTTOM - DOCK_SIZE, value))

export function PreppyDock() {
  const { state, actions } = useAssistant()
  const { open, mood, unreadCount, awaitingTicketCount, walk, walkAnchor, dockTop } = state
  const badgeCount = unreadCount + awaitingTicketCount
  const { setDockTop } = actions

  const [dragging, setDragging] = useState(false)
  const dragRef = useRef<{ offset: number; moved: boolean } | null>(null)

  // Hidden while a walkthrough runs, because Preppy is visibly flying around
  // instead of sitting here — with one exception: the tour ends by pointing at
  // the dock, and spotlighting an invisible element would be a strange finale.
  const hidden = open || (walk !== null && walkAnchor !== 'preppy-dock')

  // Keep it on screen when the window shrinks under it.
  useEffect(() => {
    const onResize = () => setDockTop(clampTop(dockTop))
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [dockTop, setDockTop])

  const onPointerDown = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    // Left button / touch / pen only — a right-click should open the context
    // menu, not start a drag it can never finish.
    if (event.button !== 0) return
    dragRef.current = { offset: event.clientY - event.currentTarget.offsetTop, moved: false }
    event.currentTarget.setPointerCapture(event.pointerId)
  }, [])

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      const drag = dragRef.current
      if (!drag) return
      const next = clampTop(event.clientY - drag.offset)
      if (!drag.moved && Math.abs(next - event.currentTarget.offsetTop) > DRAG_THRESHOLD) {
        drag.moved = true
        setDragging(true)
      }
      if (drag.moved) setDockTop(next)
    },
    [setDockTop]
  )

  const onPointerUp = useCallback(() => {
    const wasClick = dragRef.current !== null && !dragRef.current.moved
    dragRef.current = null
    setDragging(false)
    if (wasClick) actions.setOpen(!open)
  }, [actions, open])

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault()
        setDockTop(clampTop(dockTop + (event.key === 'ArrowDown' ? KEY_STEP : -KEY_STEP)))
      }
    },
    [dockTop, setDockTop]
  )

  return createPortal(
    <FeatureThemeProvider featureKey="advisoryIntel" withAntd>
      <button
        type="button"
        id="preppy-dock"
        data-tour="preppy-dock"
        aria-label={[
          'Ask Preppy',
          unreadCount > 0 ? `${unreadCount} unread ${unreadCount === 1 ? 'message' : 'messages'}` : '',
          awaitingTicketCount > 0
            ? `${awaitingTicketCount} support ${awaitingTicketCount === 1 ? 'ticket' : 'tickets'} waiting on you`
            : '',
        ]
          .filter(Boolean)
          .join(', ')
          .concat('. Arrow keys move it.')}
        aria-expanded={open}
        aria-hidden={hidden}
        tabIndex={hidden ? -1 : 0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={onKeyDown}
        // Enter/Space land here rather than on the pointer handlers.
        onClick={(event) => {
          if (event.detail !== 0) return
          actions.setOpen(!open)
        }}
        className={`preppy-dock fixed right-3.5 z-40 grid place-items-center border-0 bg-transparent p-0 ${
          dragging ? 'preppy-dock--dragging' : ''
        } ${hidden ? 'pointer-events-none scale-90 opacity-0' : 'opacity-100'}`}
        style={{ top: dockTop, width: DOCK_SIZE, height: DOCK_SIZE }}
      >
        <Badge count={badgeCount} size="small" offset={[-4, 4]}>
          <span className="pointer-events-none block">
            <Preppy mood={mood} size={DOCK_SIZE} />
          </span>
        </Badge>
      </button>
    </FeatureThemeProvider>,
    document.body
  )
}
